import { EventEmitter, Injectable, Output } from '@angular/core';
import { IPortfolioCompany } from '@common/models/IPortfolioCompany';
import { ApiService } from '@app/core/services/api.service';
import { Observable } from 'rxjs';
import { IApiStatus } from '@common/models/IApiStatus';
import { IPCCommitment } from '@common/models/IPCCommitment';
import { ITearSheet } from '@common/models/ITearSheet';
import { CommitmentState } from "@common/enums/commitment/CommitmentState";

export interface ICompanyUpdateEvent {
    action: 'create' | 'update' | 'delete' | 'save' | 'submit' | 'filePreview' | 'attachmentdelete' | 'attachmentupload';
    workflow: 'committment' | 'capitalCall' | 'distribution' | 'valuation' | 'review';
    company: IPortfolioCompany;
    apiStatus: IApiStatus | Error;
}

@Injectable({
    providedIn: 'root'
})
export class CompanyService {
    @Output() updatedCompany = new EventEmitter<ICompanyUpdateEvent>();
    constructor(private readonly apiService: ApiService) {
    }

    public get(mnemonic: string): Observable<IPortfolioCompany> {
        return this.apiService.get(`PortfolioCompany/${mnemonic}`);
    }

    public post(company: IPortfolioCompany): Observable<IPortfolioCompany> {
        return this.apiService.post(`PortfolioCompany`, company);
    }

    public getTearsheetData(mnemonic: string): Observable<ITearSheet> {
        return this.apiService.get(`PortfolioCompany/${mnemonic}/Tearsheet`);
    }
    public getAllTearsheetNames(mnemonic: string): Observable<string[]> {
        return this.apiService.get(`PortfolioCompany/${mnemonic}/Tearsheet`);
    }

    updateCompanyDetails(event: ICompanyUpdateEvent) {
        this.updatedCompany.emit(event);
    }

    getUpdatedCompany() {
        return this.updatedCompany;
    }

    activeCommitments(portfolioCompany: IPortfolioCompany): IPCCommitment {
        const todaysDate = new Date();
        const commitments = portfolioCompany.commitments.filter(c => new Date(c.effectiveDate) <= todaysDate)
        return commitments.filter(c => c.state === CommitmentState.Approved).sort((a, b) => a.effectiveDate < b.effectiveDate ? 1 : -1)[0];
    }

    totalCommitments(portfolioCompany: IPortfolioCompany): number {
        if (portfolioCompany?.commitments?.length > 0) {
            return this.activeCommitments(portfolioCompany)?.members.reduce((a, b) => a + b.amount, 0);
        }
        return 0;
    }

    availableCapital(portfolioCompany: IPortfolioCompany): number {
        return this.totalCommitments(portfolioCompany) - this.totalCapital(portfolioCompany);
    }

    totalCapital(portfolioCompany: IPortfolioCompany): number {
        return portfolioCompany ? (portfolioCompany.investedCapital || []).reduce((acc, cur) => acc + cur.amountQuantum, 0) : 0;
    }

    totalDistributions(portfolioCompany: IPortfolioCompany): number {
        return portfolioCompany ? (portfolioCompany.distributions || []).reduce((acc, cur) => acc + Math.abs(cur.amountQuantum), 0) : 0;
    }

    getPDF(data) {
        let file = new Blob([data], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL, "quilt_preview", "width=1024,height=1024,popup=yes,scrollbars=yes,resizable=yes");
    }

    uploadLogo(mnemonic: string,formData: FormData): Observable<any> {
        return this.apiService.post(`PortfolioCompany/${mnemonic}/logo`, formData)
    }
    public getAll(): Observable<IPortfolioCompany[]> {
        return this.apiService.get(`PortfolioCompany`);
    }

    upsertAdvancePf(mnemonic: string, data: any) {
        return this.apiService.put(`PortfolioCompany/${mnemonic}/AdvancePF`, data);        
    }

    getAdvancePf(mnemonic: string) {
        return this.apiService.get(`PortfolioCompany/${mnemonic}/AdvancePF`);
    }

    downloadAdvancePDF(mnemonic: string, mnemonicList: any): Observable<ArrayBuffer> {
        return this.apiService.post(`PortfolioCompany/${mnemonic}/AdvancePF/DownloadAdvancePF`,
            mnemonicList,
            { responseType: 'arraybuffer' }
        );
    }

    public getPowerBiReportFlag(mnemonic: string): Observable<IPortfolioCompany> {
        return this.apiService.get(`PortfolioCompany/PowerBiReport/flag/${mnemonic}`);
    }

}
